import { DateTime } from "luxon"
import { getCookieValue, pad } from "./helpers"
import { Agency } from "../models/agency"
import { useAgencyStore } from "@/stores/agency"

//===============================================DATE GETTERS===============================================
//===============================================
// Get a new date from a string
//===============================================
export const getters = {
    getNewDate(date: string | Date): Date {
        if (date instanceof Date) {
            return new Date(date)
        }

        if (typeof date === "string") {
            if (date.includes("/")) {
                const d = date.split("T")
                const [month, day, year] = d[0].split("/")
                return new Date(Number(year), Number(month) - 1, Number(day))
            }
            return new Date(date)
        }

        return new Date()
    },
    getDayOfWeek(date: Date) {
        const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
        return days[date.getDay()]
    },
    getHoursAndMinutes(time: string) {
        let hours = 0
        let minutes = 0
        if (time.includes("PM")) {
            hours = Number(time.split(":")[0]) === 12 ? 12 : Number(time.split(":")[0]) + 12
            minutes = Number(time.split(":")[1].split(" ")[0])
        } else if (time.includes("AM")) {
            hours = Number(time.split(":")[0]) === 12 ? 0 : Number(time.split(":")[0])
            minutes = Number(time.split(":")[1].split(" ")[0])
        } else {
            hours = Number(time.split(":")[0])
            minutes = Number(time.split(":")[1])
        }
        return [hours, minutes]
    },
    getAgencyTimezone(): string {
        const agency = JSON.parse(getCookieValue("selectedAgency") || "{}") as Agency

        // Check if timezone exists
        if (!agency.timezone) return "UTC"

        // If timezone includes UTC offset formatting, extract just the timezone name
        if (agency.timezone.includes(")")) {
            return agency.timezone.split(")")[1].trim()
        }

        // Return as is if no formatting found
        return agency.timezone
    },
    getDateInputFormat(date: Date) {
        return `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(-2)}-${`0${date.getDate()}`.slice(-2)}`
    },
    getTimeInputFormat(date: Date) {
        return `${`0${date.getHours()}`.slice(-2)}:${`0${date.getMinutes()}`.slice(-2)}`
    },
    getDueDateColors(label: string): { bg: string; text: string } {
        if (label.toLowerCase().includes("overdue") || label.toLowerCase().includes("yesterday")) {
            return { bg: "#FEE2E2", text: "#991B1B" } // red-100 / red-800
        }

        if (label.startsWith("Today") || label.startsWith("Tomorrow")) {
            return { bg: "#FFEDD5", text: "#9A3412" } // orange-100 / orange-800 (Due)
        }

        if (label.startsWith("Due in")) {
            return { bg: "#FEF3C7", text: "#92400E" } // yellow-100 / yellow-800 (Upcoming)
        }

        return { bg: "#E5E7EB", text: "#282B34" } // gray-100 / gray-800
    },
}
//===============================================DATE GETTERS END===============================================

//===============================================DATE SETTERS===============================================
export const setters = {
    setTomoroowMorning() {
        const now = new Date()
        now.setDate(now.getDate() + 1)
        now.setHours(8)
        now.setMinutes(0)
        now.setSeconds(0)
        return now
    },
    setThisAfternoon(scheduleOptions: { text: string; date: Date }[]) {
        const now = new Date()
        const afternoonDate = new Date()
        afternoonDate.setHours(13)
        afternoonDate.setMinutes(0)
        afternoonDate.setSeconds(0)

        if (afternoonDate.getTime() > now.getTime() && scheduleOptions.length < 2) {
            return {
                text: "This afternoon",
                date: afternoonDate,
            }
        }
    },
}
//===============================================DATE SETTERS END===============================================

//=============================================================CHECK DATE FUNCTIONS===============================================
export const predicates = {
    //===============================================
    // Check if a date is today
    //===============================================
    isToday(date: Date) {
        const today = new Date()
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        )
    },

    //===============================================
    // Check if a date is tomorrow
    //===============================================
    isTomorrow(date: Date) {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        return (
            date.getDate() === tomorrow.getDate() &&
            date.getMonth() === tomorrow.getMonth() &&
            date.getFullYear() === tomorrow.getFullYear()
        )
    },

    //===============================================
    // Check if a date is earlier than today
    //===============================================
    isEarlierToday(date: string, time: string) {
        if (predicates.isToday(getters.getNewDate(date))) {
            const [hours, minutes] = getters.getHoursAndMinutes(time)
            const currentHour = new Date().getHours()
            const currentMinutes = new Date().getMinutes()
            return currentHour > hours || (currentHour === hours && currentMinutes > minutes)
        }
        return false
    },

    //===============================================
    // Check if a date is this week
    //===============================================
    isThisWeek(date: Date) {
        // This function now considers the week to start on Monday 00:00 and end on Sunday 23:59 (inclusive).
        const today = new Date()

        const nextSunday = new Date()
        nextSunday.setDate(today.getDate() + ((7 - today.getDay()) % 7))
        nextSunday.setHours(23, 59, 59, 999)

        const lastSunday = new Date()
        const adjustedDayIndex = (today.getDay() === 0 ? 7 : today.getDay()) - 1
        lastSunday.setDate(today.getDate() - adjustedDayIndex)
        lastSunday.setHours(0, 0, 0, 0)

        return date.getTime() > lastSunday.getTime() && date.getTime() < nextSunday.getTime()
    },
}
//===============================================CHECK DATE FUNCTIONS END===============================================

//===============================================VALIDATORS===============================================
export const validators = {
    validHHMMstring(str: string) {
        return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(str)
    },
    checkIfQueueTimeIsInPast(dateValue: string, timeValue: string) {
        const date = new Date(dateValue)
        const hours = Number(timeValue.split(":")[0])
        const minutes = Number(timeValue.split(":")[1])
        const queueDateAndTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes)
        return queueDateAndTime < new Date()
    },
}
//===============================================VALIDATORS END===============================================

//===============================================FORMATTERS===============================================
export const formatters = {
    formatDate(date: Date, hours: string, minutes: string, period: string): string {
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()} ${hours}:${minutes} ${period}`
    },
    formatDateWithoutTime(dateString: string) {
        // Split the date string on '-' to get the year, month, and day parts
        const [year, month, day] = dateString.split("-").map(Number)
        // Return a date string in the format 'YYYY-MM-DDT00:00:00'
        if (isNaN(year) || isNaN(month) || isNaN(day)) {
            const [month, day, year] = dateString.split("/")
            return `${year}-${pad(Number(month))}-${pad(Number(day))}`
        }
        return `${year}-${pad(Number(month))}-${pad(Number(day))}`
    },
    formatDateToIso(value: string | Date): string {
        if (value === "") return value
        // Get the stored timezone from cookies, e.g. "(UTC+03:00) Europe/Bucharest"
        const agencyTimezoneValue = getCookieValue("agencyTimezone") || "UTC"
        const ianaZone = agencyTimezoneValue.replace(/^\(UTC[^)]+\)\s*/, "")

        let dt: DateTime

        if (typeof value === "string" && validators.validHHMMstring(value)) {
            const [h, m] = value.split(":").map(Number)
            // Assume today's date in the agency timezone.
            const now = DateTime.now().setZone(ianaZone)
            dt = DateTime.fromObject(
                { year: now.year, month: now.month, day: now.day, hour: h, minute: m },
                { zone: ianaZone }
            )
        } else if (typeof value === "string" && value.match(/^\d{2}\/\d{2}\/\d{4} \d{1,2}:\d{2} (AM|PM)$/i)) {
            dt = DateTime.fromFormat(value, "MM/dd/yyyy h:mm a", { zone: ianaZone })
            if (!dt.isValid) throw new Error(`Invalid formatted date: ${value}`)
        } else if (typeof value === "string" && value.includes("/")) {
            const [datePart] = value.split("T")
            const [month, day, yearWithTime] = datePart.split("/")
            const year = yearWithTime?.split(" ")[0]
            const m = Number(month),
                d = Number(day),
                y = Number(year)
            if (!isNaN(m) && !isNaN(d) && !isNaN(y)) {
                dt = DateTime.fromObject({ year: y, month: m, day: d }, { zone: ianaZone })
            } else {
                throw new Error(`Invalid date numbers: ${value}`)
            }
        } else if (value instanceof Date) {
            dt = DateTime.fromJSDate(value, { zone: ianaZone })
        } else if (typeof value === "string") {
            dt = DateTime.fromISO(value, { zone: ianaZone })
        } else {
            dt = DateTime.now().setZone(ianaZone)
        }

        if (typeof value === "string" && validators.validHHMMstring(value)) {
            const hoursStr = String(dt.hour).padStart(2, "0")
            const minutesStr = String(dt.minute).padStart(2, "0")
            return `${hoursStr}:${minutesStr}:00.000Z`
        }
        const monthStr = String(dt.month).padStart(2, "0")
        const dayStr = String(dt.day).padStart(2, "0")
        const yearStr = String(dt.year)
        const hoursStr = String(dt.hour).padStart(2, "0")
        const minutesStr = String(dt.minute).padStart(2, "0")

        return `${monthStr}/${dayStr}/${yearStr}T${hoursStr}:${minutesStr}:00.000Z`
    },

    formatDateToUtc(value: string | Date): string {
        // Get the agency timezone from your cookie (assumed to already be a valid IANA string,
        // e.g. "(UTC+03:00) Europe/Bucharest"). If needed, extract the IANA portion.
        let agencyTimezoneValue = JSON.parse(getCookieValue("selectedAgency") || "{}").timezone
        // Remove any leading text like "(UTC+03:00) " to get the IANA zone.

        try {
            if (!agencyTimezoneValue) {
                agencyTimezoneValue =
                    useAgencyStore().agency.timezone || (useAgencyStore().selectedAgency as Agency).timezone
            }
        } catch (e) {
            // get system timezone
            const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
            agencyTimezoneValue = systemTimezone
            console.warn("Error getting agency timezone, using system timezone", e)
        }

        if (!agencyTimezoneValue) {
            // get system timezone
            const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
            agencyTimezoneValue = systemTimezone
            console.warn("Error getting agency timezone, using system timezone")
        }

        const agencyTimezone = agencyTimezoneValue.replace(/^\(UTC[^)]+\)\s*/, "")

        // Get the current offset (in minutes) for that timezone using Luxon.
        const offsetInMinutes = DateTime.now().setZone(agencyTimezone).offset
        // Convert the offset to hours (could be fractional, e.g. 5.5 for IST)
        const offsetInHours = offsetInMinutes / 60

        let date: Date
        if (typeof value === "string" && value.endsWith("Z")) {
            // If the string ends with "Z", assume it's in UTC. We'll parse it manually.
            const [datePart, timePart] = value.split("T")
            const [hour, minute] = timePart.split(":").map(Number)
            let day: number, month: number, year: number
            if (value.includes("/")) {
                ;[month, day, year] = datePart.split("/").map(Number)
            } else {
                ;[year, month, day] = datePart.split("-").map(Number)
            }
            // Subtract the agency offset from the hour.
            date = new Date(year, month - 1, day, hour - offsetInHours, minute)
        } else {
            // Otherwise, parse the value directly as a Date.
            date = new Date(value)
            // Subtract the agency offset from the date's hours.
            date.setHours(date.getHours() - offsetInHours)
        }

        const yearStr = date.getFullYear()
        const monthStr = String(date.getMonth() + 1).padStart(2, "0")
        const dayStr = String(date.getDate()).padStart(2, "0")
        const hoursStr = String(date.getHours()).padStart(2, "0")
        const minutesStr = String(date.getMinutes()).padStart(2, "0")

        return `${yearStr}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}:00.000Z`
    },
    convertTo12Hour(time24: string) {
        try {
            const [hours, minutes] = time24.split(":").map(Number)

            if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
                return
            }

            const period: string = hours < 12 ? "AM" : "PM"

            let hours12: number
            if (hours === 0) {
                hours12 = 12 // Midnight
            } else if (hours > 12) {
                hours12 = hours - 12
            } else {
                hours12 = hours
            }

            // Pad minutes with leading zero if needed
            const minutesStr: string = minutes < 10 ? `0${minutes}` : `${minutes}`

            return `${hours12}:${minutesStr} ${period}`
        } catch (error) {
            return
        }
    },
    formatHumanReadableDate(date: Date | string, isWithTime = true): string {
        let dateObject: Date

        if (date instanceof Date) {
            // Check if the Date object is valid
            if (isNaN(date.getTime())) {
                console.error("Invalid Date object received:", date)
                return "Invalid Date"
            }
            dateObject = date
        } else if (typeof date === "string") {
            // Try parsing MM/DD/YYYY format first
            if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
                const parts = date.split("/")
                dateObject = new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]))
            } else {
                // Attempt standard parsing for other string formats (like ISO)
                dateObject = new Date(date)
            }
            // Check if parsing was successful
            if (isNaN(dateObject.getTime())) {
                console.error("Failed to parse date string:", date)
                return "Invalid Date" // Return "Invalid Date" if parsing failed
            }
        } else {
            console.error("Invalid input type for date:", date)
            return "Invalid Date" // Handle cases where input is neither Date nor string
        }

        const agencyTimezoneValue = JSON.parse(getCookieValue("selectedAgency") || "{}")?.timezone || "UTC"
        const ianaZone = agencyTimezoneValue.replace(/^\(UTC[^)]+\)\s*/, "")

        const dt = DateTime.fromJSDate(dateObject).setZone(ianaZone)

        // Check if Luxon conversion was successful
        if (!dt.isValid) {
            console.error("Luxon failed to create valid DateTime:", dateObject, ianaZone)
            return "Invalid Date"
        }

        // Extract hours/minutes and convert to 12-hour format.
        const hours24 = dt.hour
        const hours12 = hours24 % 12 || 12
        const minutes = dt.minute > 9 ? dt.minute.toString() : `0${dt.minute}`
        const amOrPm = hours24 >= 12 ? "PM" : "AM"
        const formattedHours = hours12 > 9 ? hours12.toString() : `0${hours12}`

        // Use your helper functions (which expect a native Date).
        const localDate = dt.toJSDate()
        if (predicates.isToday(localDate)) {
            return isWithTime ? `Today ${formattedHours}:${minutes} ${amOrPm}` : `Today`
        }
        if (predicates.isTomorrow(localDate)) {
            return isWithTime ? `Tomorrow ${formattedHours}:${minutes} ${amOrPm}` : `Tomorrow`
        }
        if (predicates.isThisWeek(localDate)) {
            return isWithTime
                ? `${getters.getDayOfWeek(localDate)} ${formattedHours}:${minutes} ${amOrPm}`
                : `${getters.getDayOfWeek(localDate)}`
        }
        if (isWithTime) {
            return `${dt.month}/${dt.day}/${dt.year} ${formattedHours}:${minutes} ${amOrPm}`
        }
        return `${dt.month}/${dt.day}/${dt.year}`
    },
    formatTaskDueDate(dueDate: Date): string {
        // Get the agency timezone from the cookie and extract the IANA zone.
        const agencyTimezoneValue = JSON.parse(getCookieValue("selectedAgency") || "{}")?.timezone || "UTC"
        const ianaZone = agencyTimezoneValue.replace(/^\(UTC[^)]+\)\s*/, "")

        // Convert dueDate to the agency local time.
        const dt = DateTime.fromJSDate(dueDate).setZone(ianaZone)
        const now = DateTime.now().setZone(ianaZone)

        // Calculate the difference in days.
        const diffMs = dt.toMillis() - now.toMillis()
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))

        // Get the hour and minute for display.
        const hours24 = dt.hour
        const period = hours24 >= 12 ? "PM" : "AM"
        const hours12 = hours24 % 12 || 12
        const hours = hours12.toString().padStart(2, "0")
        const minutes = dt.minute.toString().padStart(2, "0")

        if (diffDays > 30) {
            return formatters.formatDate(dt.toJSDate(), hours, minutes, period) // Assuming formatDate exists.
        }
        if (diffDays > 1 && diffDays <= 3) {
            return `Due in ${diffDays} Day${diffDays === 1 ? "" : "s"}`
        }
        if (diffDays === -1) {
            return `Due Yesterday`
        }
        if (diffDays > -1 && diffDays <= 1) {
            if (predicates.isToday(dt.toJSDate())) return `Today ${hours}:${minutes} ${period}`
            if (predicates.isTomorrow(dt.toJSDate())) return `Tomorrow ${hours}:${minutes} ${period}`
            return `${getters.getDayOfWeek(dt.toJSDate())} ${hours}:${minutes} ${period}`
        }
        if (diffDays <= -1 && diffDays > -3) {
            const daysOverdue = Math.abs(diffDays)
            return `${daysOverdue} Day${daysOverdue === 1 ? "" : "s"} Overdue`
        }
        return formatters.formatDate(dt.toJSDate(), hours, minutes, period)
    },
    timeSince(date: Date) {
        const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000)

        let interval = seconds / 31536000

        if (interval > 1) {
            return Math.floor(interval) + " years"
        }
        interval = seconds / 2592000
        if (interval > 1) {
            return Math.floor(interval) + " months"
        }
        interval = seconds / 86400
        if (interval > 1) {
            return Math.floor(interval) + " days"
        }
        interval = seconds / 3600
        if (interval > 1) {
            return Math.floor(interval) + " hours"
        }
        interval = seconds / 60
        if (interval > 1) {
            return Math.floor(interval) + " minutes"
        }
        if (seconds <= 0) {
            return "Now"
        }
        return Math.floor(seconds) + " seconds"
    },
    formatTaskDate(date: Date, isWithTime = true): string {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
        const year = date.getFullYear()
        const month = months[date.getMonth()]
        const day = date.getDate()

        if (isWithTime) {
            let hours = date.getHours()
            const minutes = date.getMinutes() > 9 ? date.getMinutes() : `0${date.getMinutes()}`
            const period = hours < 12 ? "AM" : "PM"

            if (hours === 0) {
                hours = 12 // Midnight
            } else if (hours > 12) {
                hours = hours - 12
            }

            return `${month} ${day}, ${year} ${hours}:${minutes} ${period}`
        }

        return `${month} ${day}, ${year}`
    },
    formatInboxDate(date: Date): string {
        const now = new Date()

        const isToday = date.toDateString() === now.toDateString()

        if (isToday) {
            let hours = date.getHours()
            const minutes = date.getMinutes().toString().padStart(2, "0")
            const period = hours >= 12 ? "PM" : "AM"

            hours = hours % 12
            hours = hours ? hours : 12 // 0 becomes 12

            return `${hours}:${minutes} ${period}`
        } else {
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
            const month = months[date.getMonth()]
            const day = date.getDate()
            return `${month} ${day}`
        }
    },
    formatNumberToMinutesAndSeconds(number: number) {
        const minutes = Math.floor(number / 60) > 9 ? Math.floor(number / 60) : `0${Math.floor(number / 60)}`
        const seconds = number % 60 > 9 ? number % 60 : `0${number % 60}`
        return `${minutes}:${seconds}`
    },
    dateTimeConverter(time: string, date?: string) {
        const timeArray = time ? getters.getHoursAndMinutes(time) : [10, 0]
        const dateObject = date ? new Date(date) : new Date()
        dateObject.setHours(Number(timeArray[0]))
        dateObject.setMinutes(Number(timeArray[1]))
        dateObject.setSeconds(0)
        return dateObject.toISOString() ?? ""
    },
}
//===============================================FORMATTERS END===============================================
